<template>
  <div
    class="templates-dropdown-wrap"
    @mousemove="mouseMoveHandle"
  >
    <div
      class="templates-search-input-wrap"
      @click.stop="focusInput"
    >
      <input
        ref="searchInput"
        v-model="searchValue"
        v-autofocus="!isMobile"
        class="templates-search-input"
        type="text"
        :placeholder="`${translate('searchTemplates')}...`"
        @keyup="checkKey"
        @compositionupdate="compositionUpdate"
        @keydown="checkDownKey"
      />
    </div>
    <vue-scroll
      ref="templatesScroll"
      class="templates-list-scroll"
      :ops="{
        scrollPanel: { scrollingX: false },
        bar: { keepShow: true, size: '4px' },
        rail: { gutterOfSide: '2px' },
      }"
    >
      <div
        v-for="(template, index) in filteredTemplates"
        :key="index"
        @mousedown.prevent
      >
        <FilterDropdownItem
          :index="index"
          :item="template"
          :selected-item-index="selectedIndex"
          :selected-item-check="selectedTemplateIndex"
          @item-hover="itemHover(index)"
          @scroll-to-item="scrollToItem"
          @item-click="selectSearchResult(template, index)"
          @item-right-side-click="selectSearchResult(template, index)"
        />
      </div>
      <div
        class="templates-notfound-wrap"
        @click.stop
      >
        <p class="text-text-placeholder">{{ templateNotFound ? translate('noTemplates') : '' }}</p>
        <p
          class="text-text-placeholder underline cursor-pointer"
          @click="goToManageTemplates"
        >
          {{ translate('manageTemplates') }}
        </p>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import FilterDropdownItem from '@/components/widgets/filters/FilterDropdownItem';
import inputDropdown from '@/mixins/inputDropdown';
import scrollToElementInVuescroll from '@/utilities/scrollToElementInVuescroll';
import { ResourceType } from '@/types/resources';

export default {
  name: 'TemplatesDropdown',
  components: {
    FilterDropdownItem,
  },
  mixins: [inputDropdown],
  props: {
    selectedTemplateId: {
      type: String,
      required: true,
    },
    resourceType: {
      type: String,
      required: false,
      default: ResourceType.Card,
    },

    editorId: {
      type: String,
      required: false,
      default: '',
    },

    filterTemplates: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  emits: ['selectTemplate', 'hidePopup'],
  data() {
    return {
      searchValue: '',
      inputFocusTimeout: null,
    };
  },
  computed: {
    isCardResource() {
      return this.resourceType === ResourceType.Card;
    },
    templates() {
      return this.isCardResource
        ? this.$store.getters.getCardTemplates
        : this.$store.getters.getPageTemplates;
    },
    filteredTemplates() {
      return this.dropdownItems.filter(
        (item) =>
          item.label?.toLowerCase().includes(this.searchValue.toLowerCase()) &&
          !this.filterTemplates.includes(item.id)
      );
    },
    searchNotEmpty() {
      return this.searchValue !== '';
    },
    projectIds() {
      return this.$store.getters.getProjectOrder;
    },
    templateNotFound() {
      return (this.searchNotEmpty && !this.filteredTemplates.length) || !this.dropdownItems.length;
    },
    dropdownItems() {
      return Object.values(this.templates)
        .filter((template) => template.project_ids.some((id) => this.projectIds.includes(id)))
        .map((template) => ({
          id: template.id,
          label: template.title,
          checkbox: false,
          lighter: true,
          icon: template?.icon?.src,
          customIcon: !!template?.icon?.src,
          customIconColor: template?.icon?.color,
          iconType: template?.icon?.type || this.iconEnum,
        }));
    },
    searchResults() {
      return this.filteredTemplates;
    },
    selectedTemplateIndex() {
      return this.searchResults.findIndex((result) => result.id === this.selectedTemplateId);
    },

    editor() {
      return this.$store.getters.editor(this.editorId);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.focusInput();
    });
  },
  unmounted() {
    clearTimeout(this.inputFocusTimeout);
  },
  methods: {
    focusInput() {
      clearTimeout(this.inputFocusTimeout);
      this.inputFocusTimeout = setTimeout(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      }, 100);
    },
    scrollToItem(event) {
      scrollToElementInVuescroll(event, this.$refs.templatesScroll);
    },
    itemHover(index) {
      this.setSelectedIndex(index);
    },
    selectSearchResult(template, index, _, isEnterKey) {
      this.$emit('selectTemplate', { template, index, isEnterKey });
    },
    handleEsc(event) {
      this.$emit('hidePopup', event);
    },
    goToManageTemplates() {
      if (this.resourceType === ResourceType.Page) {
        const tr = this.editor?.view?.state?.tr || null;
        if (tr) {
          tr.setMeta('contentMenu', {
            type: 'remove',
          });
          this.editor.view.dispatch(tr);
          this.$emit('hidePopup', event);
        }
      }
      const { card: cardTemplate, page: pageTemplate } = this.$constants.settingsTemplate;
      const routeName = this.isCardResource ? cardTemplate.root.name : pageTemplate.root.name;
      this.$router.push({ name: routeName });
    },
  },
};
</script>

<style lang="scss">
.templates-dropdown-wrap {
  @apply min-w-[245px]
  max-w-[600px]
  box-border
  flex
  flex-col
  max-h-[282px]
  h-auto
  rounded-lg
  bg-important-bg
  overflow-hidden
  shadow-md
  border
  border-solid
  border-important-border;

  .templates-search-input-wrap {
    border-bottom: 1px solid;
    @apply w-full
    box-border
    flex
    items-center
    pt-[9px]
    pb-[10px]
    px-[15px]
    rounded-t-lg
    border-dividers-default
    cursor-text;

    .templates-search-input {
      @apply border-none
      box-border
      outline-none
      font-normal
      text-sm
      w-full
      text-text-default
      bg-important-bg;

      &::placeholder {
        @apply text-text-placeholder;
      }
    }
  }

  .templates-list-scroll {
    @apply flex
    box-border
    flex-col
    overflow-hidden;

    .templates-notfound-wrap {
      @apply flex
      flex-row
      py-[10px]
      px-[15px]
      text-sm
      rounded-lg
      gap-1;
    }
  }
}
</style>
